import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { certificateService } from '@/services/certificate.service';
import { trailsService } from '@/services/trails.service';
import { LoadingSpinner } from '@/components';
import CourseSurveyModal from '@/components/CourseSurveyModal';
import { useToast } from '@/contexts/ToastContext';
import type { Certificate } from '@/types/certificate';

export default function CertificatePage() {
  const { id: trailId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [certificate, setCertificate] = useState<Certificate | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [surveyAnswered, setSurveyAnswered] = useState<boolean | null>(null);

  useEffect(() => {
    if (!trailId) return;
    setIsLoading(true);
    Promise.all([
      certificateService.generate(trailId),
      trailsService.getCourseSurvey(trailId).catch(() => ({ answered: true, response: null })),
    ])
      .then(([cert, surveyStatus]) => {
        setCertificate(cert);
        setSurveyAnswered(surveyStatus.answered);
      })
      .catch((err) => {
        const msg = err?.response?.data?.error || 'Erro ao gerar certificado';
        setError(msg);
      })
      .finally(() => setIsLoading(false));
  }, [trailId]);

  const handleDownloadPDF = async () => {
    if (!certificate) return;
    setIsGenerating(true);
    try {
      if (certificate.download_url) {
        window.open(certificate.download_url, '_blank');
      } else {
        const url = await certificateService.getDownloadUrl(certificate.id);
        window.open(url, '_blank');
      }
    } catch {
      setError('Erro ao baixar certificado');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleShare = async () => {
    if (!certificate) return;
    const verifyUrl = `${window.location.origin}/verificar-certificado/${certificate.verification_code}`;
    try {
      await navigator.clipboard.writeText(verifyUrl);
      showToast('Link de verificacao copiado!', 'success');
    } catch {
      prompt('Copie o link:', verifyUrl);
    }
  };

  const handleValidate = () => {
    if (!certificate) return;
    navigate(`/verificar-certificado/${certificate.verification_code}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error || !certificate) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <span className="material-symbols-outlined text-5xl text-content-tertiary">error_outline</span>
        <p className="text-content-secondary text-lg">{error || 'Certificado nao encontrado'}</p>
        <Link to={`/trilhas/${trailId}`} className="text-accent hover:underline">
          Voltar para a trilha
        </Link>
      </div>
    );
  }

  const showSurveyModal = surveyAnswered === false;

  return (
    <div className="flex flex-col items-center p-6 md:p-12">
      {showSurveyModal && trailId && (
        <CourseSurveyModal
          trailId={trailId}
          courseName={certificate.trail_title}
          onSubmitted={() => setSurveyAnswered(true)}
        />
      )}
      <div
        className={`max-w-5xl w-full flex flex-col gap-10 transition-all ${
          showSurveyModal ? 'blur-sm pointer-events-none select-none' : ''
        }`}
        aria-hidden={showSurveyModal}
      >
        {/* Back Button */}
        <Link
          to={`/trilhas/${trailId}`}
          className="flex items-center gap-2 text-content-tertiary hover:text-accent transition-colors font-medium"
        >
          <span className="material-symbols-outlined">arrow_back</span>
          Voltar para a Trilha
        </Link>

        {/* Certificate Card */}
        <div className="relative w-full aspect-[1.414/1] bg-gradient-to-br from-white/5 to-white/10 rounded-2xl overflow-hidden flex flex-col items-center justify-center p-12 md:p-20 border border-white/10 shadow-2xl">
          {/* Background Pattern */}
          <div
            className="absolute inset-0 opacity-5 pointer-events-none"
            style={{
              backgroundImage:
                'radial-gradient(circle, #FCB428 1px, transparent 1px), radial-gradient(circle, #FCB428 1px, transparent 1px)',
              backgroundSize: '20px 20px',
              backgroundPosition: '0 0, 10px 10px',
            }}
          />

          {/* Top Left Badge */}
          <div className="absolute top-12 left-12 flex items-center gap-3 text-content-muted">
            <div className="size-8">
              <svg fill="none" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M24 4C25.7818 14.2173 33.7827 22.2182 44 24C33.7827 25.7818 25.7818 33.7827 24 44C22.2182 33.7827 14.2173 25.7818 4 24C14.2173 22.2182 22.2182 14.2173 24 4Z"
                  fill="currentColor"
                />
              </svg>
            </div>
            <p className="font-bold text-sm tracking-widest uppercase">Certificado Oficial</p>
          </div>

          {/* Certificate Content */}
          <div className="relative z-10 flex flex-col items-center text-center max-w-3xl">
            <span className="text-cyan-accent font-bold tracking-[0.3em] uppercase text-sm mb-6">
              Certificado de Conclusao
            </span>
            <h3 className="text-content-muted text-lg md:text-xl font-medium mb-2">
              Certificamos que
            </h3>
            <h1 className="text-content-primary text-4xl md:text-6xl font-black mb-8 tracking-tight">
              {certificate.user_name}
            </h1>
            <div className="w-24 h-1 bg-cyan-accent/30 mb-8" />
            <p className="text-content-secondary text-lg md:text-xl leading-relaxed">
              Concluiu com exito a trilha de aprendizado <br />
              <span className="text-cyan-accent font-bold">{certificate.trail_title}</span> <br />
              com carga horaria total de {certificate.hours} horas.
            </p>
          </div>

          {/* Bottom Info */}
          <div className="absolute bottom-12 inset-x-12 flex justify-between items-end">
            <div className="flex flex-col gap-1">
              <p className="text-content-muted text-2xs uppercase font-bold tracking-widest">
                Data de Emissao
              </p>
              <p className="text-content-primary font-bold">{formatDate(certificate.issued_at)}</p>
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="size-20 flex items-center justify-center">
                <div className="size-16 text-cyan-accent/80">
                  <svg fill="none" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M24 4C25.7818 14.2173 33.7827 22.2182 44 24C33.7827 25.7818 25.7818 33.7827 24 44C22.2182 33.7827 14.2173 25.7818 4 24C14.2173 22.2182 22.2182 14.2173 24 4Z"
                      fill="currentColor"
                    />
                  </svg>
                </div>
              </div>
              <p className="text-content-muted text-2xs font-mono">ID: {certificate.verification_code}</p>
            </div>
            <div className="flex flex-col gap-1 text-right">
              <p className="text-content-muted text-2xs uppercase font-bold tracking-widest">
                Assinatura Digital
              </p>
              <div className="h-8 border-b border-cyan-accent/20 mb-1 flex items-center justify-end">
                <p className="font-display italic text-cyan-accent font-medium">
                  h.lab
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <button
            onClick={handleDownloadPDF}
            disabled={isGenerating}
            data-testid="certificate-download"
            className="flex items-center justify-center gap-3 px-10 h-14 bg-accent hover:bg-accent-hover text-accent-contrast rounded-xl font-bold transition-all shadow-lg shadow-accent/20 w-full sm:w-auto disabled:opacity-50"
          >
            {isGenerating ? (
              <LoadingSpinner size="small" />
            ) : (
              <span className="material-symbols-outlined">download</span>
            )}
            {isGenerating ? 'Gerando...' : 'Baixar PDF'}
          </button>
          <button
            onClick={handleShare}
            className="flex items-center justify-center gap-3 px-10 h-14 bg-white/5 border-2 border-cyan-accent text-cyan-accent hover:bg-cyan-accent/5 rounded-xl font-bold transition-all w-full sm:w-auto"
          >
            <span className="material-symbols-outlined">share</span>
            Compartilhar
          </button>
          <button
            onClick={handleValidate}
            className="flex items-center justify-center gap-3 px-10 h-14 bg-white/5 border border-white/10 text-content-muted hover:text-content-primary rounded-xl font-bold transition-all w-full sm:w-auto"
          >
            <span className="material-symbols-outlined">verified</span>
            Validar Autenticidade
          </button>
        </div>
      </div>
    </div>
  );
}
